'use client';

import React, { useState } from 'react';
import { ChevronDown, ChevronUp, Eye, Printer } from 'lucide-react';
import { MOCK_SALES, PAYMENT_STATUS_STYLES, INVOICE_STATUS_STYLES, formatUGX, SaleRecord } from './salesData';

interface Props {
  farmFilter: string;
  statusFilter: string;
  search: string;
  canManage?: boolean;
}

function InvoiceDetailModal({ sale, onClose }: { sale: SaleRecord; onClose: () => void }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div>
            <h2 className="text-lg font-bold text-gray-900">{sale.invoiceNumber}</h2>
            <p className="text-xs text-gray-500 mt-0.5">{sale.farm} — {sale.branch}</p>
          </div>
          <div className="flex gap-2">
            <button className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold border border-gray-200 rounded-lg text-gray-600 hover:bg-gray-50 transition-colors">
              <Printer size={13} /> Print
            </button>
            <button onClick={onClose} className="px-3 py-1.5 text-xs font-semibold text-gray-500 hover:text-gray-700 transition-colors">Close</button>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
          {/* Customer & Dates */}
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div className="space-y-1">
              <p className="text-xs font-bold text-gray-500 uppercase tracking-wide">Bill To</p>
              <p className="font-semibold text-gray-900">{sale.customerName}</p>
              <p className="text-gray-500">{sale.customerPhone}</p>
              <p className="text-gray-500">{sale.customerAddress}</p>
            </div>
            <div className="space-y-1 text-right">
              <p className="text-xs font-bold text-gray-500 uppercase tracking-wide">Invoice Details</p>
              <p className="text-gray-700">Date: <span className="font-semibold">{sale.date}</span></p>
              <p className="text-gray-700">Due: <span className="font-semibold">{sale.dueDate}</span></p>
              <p className="text-gray-700">Payment: <span className="font-semibold">{sale.paymentMethod}</span></p>
            </div>
          </div>
          {/* Status badges */}
          <div className="flex gap-2">
            <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${INVOICE_STATUS_STYLES[sale.invoiceStatus].bg} ${INVOICE_STATUS_STYLES[sale.invoiceStatus].text}`}>
              Invoice: {INVOICE_STATUS_STYLES[sale.invoiceStatus].label}
            </span>
            <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${PAYMENT_STATUS_STYLES[sale.paymentStatus].bg} ${PAYMENT_STATUS_STYLES[sale.paymentStatus].text}`}>
              Payment: {PAYMENT_STATUS_STYLES[sale.paymentStatus].label}
            </span>
          </div>
          {/* Line Items */}
          <div className="border border-gray-200 rounded-xl overflow-hidden">
            <table className="w-full text-xs">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="text-left px-3 py-2 font-semibold text-gray-600">Description</th>
                  <th className="text-right px-3 py-2 font-semibold text-gray-600">Qty</th>
                  <th className="text-left px-3 py-2 font-semibold text-gray-600">Unit</th>
                  <th className="text-right px-3 py-2 font-semibold text-gray-600">Unit Price</th>
                  <th className="text-right px-3 py-2 font-semibold text-gray-600">Total</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {sale.lineItems.map(li => (
                  <tr key={li.id} className="bg-white">
                    <td className="px-3 py-2 text-gray-800">{li.description}</td>
                    <td className="px-3 py-2 text-right text-gray-700">{li.quantity.toLocaleString()}</td>
                    <td className="px-3 py-2 text-gray-500">{li.unit}</td>
                    <td className="px-3 py-2 text-right text-gray-700">{li.unitPrice.toLocaleString()}</td>
                    <td className="px-3 py-2 text-right font-semibold text-gray-900">{li.totalPrice.toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {/* Totals */}
          <div className="flex justify-end">
            <div className="w-56 space-y-1.5 text-sm">
              <div className="flex justify-between text-gray-600"><span>Subtotal</span><span>{formatUGX(sale.subtotal)}</span></div>
              {sale.discount > 0 && <div className="flex justify-between text-gray-500"><span>Discount</span><span>− {formatUGX(sale.discount)}</span></div>}
              <div className="flex justify-between font-bold text-gray-900 border-t border-gray-200 pt-1.5"><span>Total</span><span className="text-blue-700">{formatUGX(sale.totalAmount)}</span></div>
              <div className="flex justify-between text-green-700 font-semibold"><span>Paid</span><span>{formatUGX(sale.amountPaid)}</span></div>
              {sale.balance > 0 && <div className="flex justify-between text-red-600 font-bold"><span>Balance Due</span><span>{formatUGX(sale.balance)}</span></div>}
            </div>
          </div>
          {sale.notes && (
            <div className="bg-gray-50 rounded-lg px-4 py-3 text-xs text-gray-600 border border-gray-100">
              <span className="font-semibold">Notes: </span>{sale.notes}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function SalesTable({ farmFilter, statusFilter, search, canManage = false }: Props) {
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [viewInvoice, setViewInvoice] = useState<SaleRecord | null>(null);

  const filtered = MOCK_SALES.filter(s => {
    const matchFarm = !farmFilter || s.farm === farmFilter;
    const matchStatus = !statusFilter || s.paymentStatus === statusFilter;
    const q = search.toLowerCase();
    const matchSearch = !q || s.invoiceNumber.toLowerCase().includes(q) || s.customerName.toLowerCase().includes(q);
    return matchFarm && matchStatus && matchSearch;
  });

  return (
    <>
      <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="text-left px-4 py-3 font-semibold text-gray-600 text-xs">Invoice #</th>
              <th className="text-left px-4 py-3 font-semibold text-gray-600 text-xs">Customer</th>
              <th className="text-left px-4 py-3 font-semibold text-gray-600 text-xs">Farm / Branch</th>
              <th className="text-left px-4 py-3 font-semibold text-gray-600 text-xs">Date</th>
              <th className="text-right px-4 py-3 font-semibold text-gray-600 text-xs">Total</th>
              <th className="text-right px-4 py-3 font-semibold text-gray-600 text-xs">Balance</th>
              <th className="text-left px-4 py-3 font-semibold text-gray-600 text-xs">Status</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {filtered.length === 0 && (
              <tr><td colSpan={8} className="text-center py-12 text-gray-400 text-sm">No invoices found</td></tr>
            )}
            {filtered.map(sale => {
              const isExpanded = expandedId === sale.id;
              const ps = PAYMENT_STATUS_STYLES[sale.paymentStatus];
              return (
                <React.Fragment key={sale.id}>
                  <tr className="hover:bg-gray-50 transition-colors cursor-pointer" onClick={() => setExpandedId(isExpanded ? null : sale.id)}>
                    <td className="px-4 py-3 font-mono text-xs font-semibold text-blue-700">{sale.invoiceNumber}</td>
                    <td className="px-4 py-3">
                      <p className="font-semibold text-gray-900 text-xs">{sale.customerName}</p>
                      <p className="text-gray-400 text-xs">{sale.customerPhone}</p>
                    </td>
                    <td className="px-4 py-3">
                      <p className="text-xs text-gray-700">{sale.farm}</p>
                      <p className="text-xs text-gray-400">{sale.branch}</p>
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-600">{sale.date}</td>
                    <td className="px-4 py-3 text-right text-xs font-semibold text-gray-900">{formatUGX(sale.totalAmount)}</td>
                    <td className="px-4 py-3 text-right text-xs font-semibold">
                      <span className={sale.balance > 0 ? 'text-red-600' : 'text-green-600'}>{formatUGX(sale.balance)}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${ps.bg} ${ps.text}`}>{ps.label}</span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1">
                        <button onClick={e => { e.stopPropagation(); setViewInvoice(sale); }}
                          className="p-1.5 rounded-lg hover:bg-blue-50 text-gray-400 hover:text-blue-600 transition-colors">
                          <Eye size={14} />
                        </button>
                        {isExpanded ? <ChevronUp size={14} className="text-gray-400" /> : <ChevronDown size={14} className="text-gray-400" />}
                      </div>
                    </td>
                  </tr>
                  {isExpanded && (
                    <tr className="bg-blue-50/40">
                      <td colSpan={8} className="px-6 py-4">
                        <div className="grid grid-cols-2 gap-6 text-xs">
                          <div>
                            <p className="font-bold text-gray-700 mb-2">Line Items</p>
                            <div className="space-y-1">
                              {sale.lineItems.map(li => (
                                <div key={li.id} className="flex justify-between text-gray-600">
                                  <span>{li.description} ({li.quantity} {li.unit})</span>
                                  <span className="font-semibold">{formatUGX(li.totalPrice)}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                          <div className="space-y-1">
                            <p className="font-bold text-gray-700 mb-2">Payment Summary</p>
                            <div className="flex justify-between text-gray-600"><span>Total Amount</span><span className="font-semibold">{formatUGX(sale.totalAmount)}</span></div>
                            <div className="flex justify-between text-green-700"><span>Amount Paid</span><span className="font-semibold">{formatUGX(sale.amountPaid)}</span></div>
                            {sale.balance > 0 && <div className="flex justify-between text-red-600 font-bold"><span>Balance Due</span><span>{formatUGX(sale.balance)}</span></div>}
                            <div className="flex justify-between text-gray-500 pt-1"><span>Method</span><span>{sale.paymentMethod}</span></div>
                            <div className="flex justify-between text-gray-500"><span>Due Date</span><span>{sale.dueDate}</span></div>
                            {sale.notes && <p className="text-gray-500 pt-1 italic">{sale.notes}</p>}
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {viewInvoice && <InvoiceDetailModal sale={viewInvoice} onClose={() => setViewInvoice(null)} />}
    </>
  );
}
